<template>
  <div class="container-fluid">
    <!-- Bread-crumb -->
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>

    <div class="row clearfix">
      <div class="col-lg-12">
        <!-- Find customer -->
        <div class="card card-top-line">
          <div class="header">
            <h2>Find Customer</h2>
          </div>
          <div class="body pt-0">
            <div class="mb-2">
              <label class="text-muted small mb-1">Search</label>
              <b-input-group>
                <b-form-input
                  v-model.trim="customerSearchQuery"
                  placeholder="Search by PayAngel ID, email, phone, name, or customer UUID"
                  @keyup.enter="searchCustomers"
                ></b-form-input>
                <b-input-group-append>
                  <b-button
                    variant="primary"
                    :disabled="searching || (customerSearchQuery || '').length < 2"
                    @click="searchCustomers"
                  >
                    <b-spinner v-if="searching" small class="mr-1"></b-spinner>
                    Search
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small class="text-muted">
                Enter at least 2 characters. Or open this page from a customer's profile to prefill.
              </small>
            </div>

            <b-alert :show="!!searchMessage" variant="light" class="border py-2 my-2">
              {{ searchMessage }}
            </b-alert>

            <b-table
              v-if="customerSearchResults.length"
              responsive
              hover
              head-variant="light"
              :items="customerSearchResults"
              :fields="searchFields"
              class="mb-3"
            >
              <template #cell(name)="row">{{ row.item.name || "—" }}</template>
              <template #cell(identifier)="row">{{ row.item.payangel_id || row.item.custrefid || "—" }}</template>
              <template #cell(email)="row">{{ row.item.email || "—" }}</template>
              <template #cell(phone)="row">{{ row.item.phone || "—" }}</template>
              <template #cell(action)="row">
                <b-button size="sm" variant="primary" @click="selectCustomer(row.item)">Select</b-button>
              </template>
            </b-table>

            <div class="mb-0">
              <label class="text-muted small mb-1">Customer ID (PayAngel customer UUID)</label>
              <b-input-group>
                <b-form-input
                  v-model.trim="customerId"
                  placeholder="e.g. 3b4dc7d5-00cd-40b0-ab45-...."
                ></b-form-input>
                <b-input-group-append>
                  <b-button variant="outline-secondary" :disabled="!customerId || loading" @click="loadCustomer">
                    Load statements
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small v-if="selectedCustomerLabel" class="text-success">Selected: {{ selectedCustomerLabel }}</small>
              <small v-else class="text-muted">Pick a customer above, or paste the customer UUID.</small>
            </div>
          </div>
        </div>

        <!-- Generate a statement -->
        <div class="card card-top-line">
          <div class="header">
            <h2>Generate a Statement</h2>
          </div>
          <div class="body pt-0">
            <div class="row">
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">From <span class="text-danger">*</span></label>
                <b-form-input v-model="filters.start_date" type="date"></b-form-input>
              </div>
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">To <span class="text-danger">*</span></label>
                <b-form-input v-model="filters.end_date" type="date"></b-form-input>
              </div>
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">Format</label>
                <b-form-select v-model="filters.format" :options="formatOptions"></b-form-select>
              </div>
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">Transaction type</label>
                <b-form-select v-model="filters.transaction_type" :options="transactionTypeOptions"></b-form-select>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="text-muted small mb-1">Beneficiary (optional)</label>
                <b-input-group>
                  <b-form-input
                    v-model.trim="beneficiarySearchQuery"
                    :disabled="!customerId"
                    placeholder="Search beneficiary by name"
                    @keyup.enter="searchBeneficiaries"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button
                      variant="outline-secondary"
                      :disabled="!customerId || beneficiaryLoading"
                      @click="searchBeneficiaries"
                    >
                      <b-spinner v-if="beneficiaryLoading" small></b-spinner>
                      <span v-else>Search</span>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
                <b-form-select
                  v-model="filters.beneficiary_id"
                  :options="beneficiaryOptions"
                  :disabled="!customerId"
                  class="mt-1"
                ></b-form-select>
                <small v-if="!customerId" class="text-muted">Select a customer first.</small>
                <small v-else-if="beneficiaryMessage" class="text-muted">{{ beneficiaryMessage }}</small>
              </div>
              <div class="col-md-4 mb-3">
                <label class="text-muted small mb-1">Reference contains</label>
                <b-form-input v-model.trim="filters.reference" placeholder="reference / reason"></b-form-input>
              </div>
              <div class="col-md-2 mb-3">
                <label class="text-muted small mb-1">Min amount</label>
                <b-form-input v-model="filters.min_amount" type="number" step="0.01"></b-form-input>
              </div>
              <div class="col-md-2 mb-3">
                <label class="text-muted small mb-1">Max amount</label>
                <b-form-input v-model="filters.max_amount" type="number" step="0.01"></b-form-input>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="text-muted small mb-1">Status</label>
                <b-form-checkbox-group v-model="filters.status_buckets" :options="statusOptions"></b-form-checkbox-group>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small mb-1">Payout method</label>
                <b-form-checkbox-group v-model="filters.payout_methods" :options="payoutOptions"></b-form-checkbox-group>
              </div>
            </div>

            <div class="row">
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">Destination countries</label>
                <b-form-input v-model.trim="destinationCountries" placeholder="ISO codes, e.g. GH,NG"></b-form-input>
              </div>
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">Source countries</label>
                <b-form-input v-model.trim="sourceCountries" placeholder="ISO codes, e.g. GB"></b-form-input>
              </div>
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">Sending currencies</label>
                <b-form-input v-model.trim="sendingCurrencies" placeholder="e.g. GBP,USD"></b-form-input>
              </div>
              <div class="col-md-3 mb-3">
                <label class="text-muted small mb-1">Receiving currencies</label>
                <b-form-input v-model.trim="receivingCurrencies" placeholder="e.g. GHS"></b-form-input>
              </div>
            </div>

            <div class="d-flex flex-wrap justify-content-between align-items-center">
              <b-form-checkbox v-model="filters.include_hidden" class="mb-2">
                Include hidden transactions
              </b-form-checkbox>
              <b-button variant="success" class="mb-2" :disabled="!canGenerate || loading" @click="generate">
                <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                Generate statement
              </b-button>
            </div>

            <b-alert
              :show="!!message"
              :variant="messageType === 'error' ? 'danger' : 'success'"
              class="mt-2 mb-0"
            >
              {{ message }}
            </b-alert>
          </div>
        </div>

        <!-- Generated statements -->
        <div class="card card-top-line">
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Generated Statements</h2>
            <b-button size="sm" variant="outline-secondary" :disabled="!customerId" @click="refreshList">
              <i class="fa fa-refresh"></i> Refresh
            </b-button>
          </div>
          <div class="body pt-0">
            <b-table
              responsive
              hover
              head-variant="light"
              show-empty
              :items="statements"
              :fields="statementFields"
              empty-text="No statements yet for this customer."
              class="mb-0"
            >
              <template #cell(period)="row">{{ row.item.start_date }} &rarr; {{ row.item.end_date }}</template>
              <template #cell(format)="row">
                <span class="text-uppercase">{{ row.item.format }}</span>
              </template>
              <template #cell(status)="row">
                <span class="badge" :class="statusBadgeClass(row.item.status)">{{ row.item.status }}</span>
                <small v-if="row.item.status === 'failed' && row.item.error_message" class="text-danger d-block">
                  {{ row.item.error_message }}
                </small>
              </template>
              <template #cell(created_at)="row">{{ formatDate(row.item.created_at) }}</template>
              <template #cell(ready_at)="row">{{ row.item.ready_at ? formatDate(row.item.ready_at) : "—" }}</template>
              <template #cell(download)="row">
                <b-button
                  v-if="row.item.statement_url"
                  :href="row.item.statement_url"
                  target="_blank"
                  rel="noopener"
                  size="sm"
                  variant="success"
                >
                  <i class="fa fa-download"></i> Download
                </b-button>
                <span v-else-if="['pending', 'processing'].includes(row.item.status)" class="text-muted">Generating…</span>
                <span v-else>—</span>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadCrumb from "@/components/BreadCrumb.vue";

export default {
  name: "StatementComponent",
  components: {
    BreadCrumb,
  },
  data() {
    return {
      customerId: this.$route.query.customer_id || "",
      customerSearchQuery: "",
      customerSearchResults: [],
      searching: false,
      searchMessage: "",
      selectedCustomerLabel: "",
      beneficiarySearchQuery: "",
      beneficiaryOptions: [{ value: "", text: "All beneficiaries" }],
      beneficiaryLoading: false,
      beneficiaryMessage: "",
      statements: [],
      message: "",
      messageType: "success",
      pollTimer: null,
      // comma-separated text inputs, mapped to arrays on submit
      destinationCountries: "",
      sourceCountries: "",
      sendingCurrencies: "",
      receivingCurrencies: "",
      formatOptions: [
        { value: "pdf", text: "PDF" },
        { value: "excel", text: "Excel" },
      ],
      transactionTypeOptions: [
        { value: null, text: "All" },
        { value: "beneficiary", text: "Beneficiary transfers" },
        { value: "merchant", text: "Merchant payments" },
      ],
      statusOptions: [
        "Completed",
        "Pending",
        "Processing",
        "Awaiting Funds",
        "Incomplete",
        "Cancelled",
        "Failed",
        "Refunded",
        "Awaiting Approval",
      ],
      payoutOptions: [
        "Mobile Money",
        "Bank Deposit",
        "Payangel Wallet",
        "Wallet",
        "Cash Pickup",
        "Airtime",
        "Credit Card",
        "Debit Card",
        "Wallet Refund",
      ],
      searchFields: [
        { key: "name", label: "Name" },
        { key: "identifier", label: "PayAngel ID" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "action", label: "", thClass: "text-right", tdClass: "text-right" },
      ],
      statementFields: [
        { key: "period", label: "Period" },
        { key: "format", label: "Format" },
        { key: "status", label: "Status" },
        { key: "created_at", label: "Requested" },
        { key: "ready_at", label: "Ready" },
        { key: "download", label: "Download", thClass: "text-right", tdClass: "text-right" },
      ],
      filters: {
        start_date: "",
        end_date: "",
        format: "pdf",
        beneficiary_id: "",
        transaction_type: null,
        reference: "",
        min_amount: "",
        max_amount: "",
        status_buckets: [],
        payout_methods: [],
        include_hidden: false,
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters["statement/statementLoading"];
    },
    canGenerate() {
      return this.customerId && this.filters.start_date && this.filters.end_date;
    },
  },
  created() {
    if (this.customerId) this.loadCustomer();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async searchCustomers() {
      const q = (this.customerSearchQuery || "").trim();
      if (q.length < 2) return;
      this.searching = true;
      this.searchMessage = "";
      this.customerSearchResults = [];
      const res = await this.$store.dispatch("statement/searchCustomers", q);
      this.searching = false;
      if (res && res.status === 200 && res.data) {
        this.customerSearchResults = res.data.data || [];
        this.searchMessage = this.customerSearchResults.length ? "" : "No customers found for that search.";
      } else if (res && res.status === 401) {
        this.searchMessage = "You do not have permission to search customers.";
      } else {
        this.searchMessage = this.extractError(res) || "Search failed.";
      }
    },
    selectCustomer(c) {
      this.customerId = c.customer_id;
      this.selectedCustomerLabel = [c.name, c.payangel_id || c.custrefid, c.email].filter(Boolean).join(" · ");
      this.customerSearchResults = [];
      this.searchMessage = "";
      this.loadCustomer();
    },
    // Load everything scoped to the current customer: their statements AND their
    // beneficiaries (for the picker). Kept out of refreshList() because that is
    // polled every 5s and must not re-fetch beneficiaries each tick.
    async loadCustomer() {
      this.filters.beneficiary_id = "";
      this.beneficiarySearchQuery = "";
      this.beneficiaryOptions = [{ value: "", text: "All beneficiaries" }];
      this.beneficiaryMessage = "";
      await this.refreshList();
      this.fetchBeneficiaries("");
    },
    async fetchBeneficiaries(q) {
      if (!this.customerId) return;
      this.beneficiaryLoading = true;
      this.beneficiaryMessage = "";
      const res = await this.$store.dispatch("statement/searchBeneficiaries", {
        customerId: this.customerId,
        q: q || "",
      });
      this.beneficiaryLoading = false;
      const base = [{ value: "", text: "All beneficiaries" }];
      if (res && res.status === 200 && res.data) {
        const list = res.data.data || [];
        this.beneficiaryOptions = base.concat(
          list.map((b) => {
            const label = b.name || b.nickname || b.beneficiary_id;
            const suffix = b.nickname && b.name ? ` (${b.nickname})` : "";
            return { value: b.beneficiary_id, text: `${label}${suffix}` };
          }),
        );
        this.beneficiaryMessage = list.length
          ? `${list.length} beneficiary(ies).`
          : "No beneficiaries found for this customer.";
      } else if (res && res.status === 401) {
        this.beneficiaryOptions = base;
        this.beneficiaryMessage = "You do not have permission to search beneficiaries.";
      } else {
        this.beneficiaryOptions = base;
        this.beneficiaryMessage = this.extractError(res) || "Could not load beneficiaries.";
      }
    },
    searchBeneficiaries() {
      this.fetchBeneficiaries(this.beneficiarySearchQuery);
    },
    splitCsv(value) {
      if (!value) return undefined;
      const parts = value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      return parts.length ? parts : undefined;
    },
    buildPayload() {
      const f = this.filters;
      const payload = {
        customer_id: this.customerId,
        start_date: f.start_date,
        end_date: f.end_date,
        format: f.format,
      };
      if (f.beneficiary_id) payload.beneficiary_id = f.beneficiary_id;
      if (f.reference) payload.reference = f.reference;
      if (f.transaction_type) payload.transaction_type = f.transaction_type;
      if (f.min_amount !== "" && f.min_amount !== null) payload.min_amount = String(f.min_amount);
      if (f.max_amount !== "" && f.max_amount !== null) payload.max_amount = String(f.max_amount);
      if (f.status_buckets.length) payload.status_buckets = f.status_buckets;
      if (f.payout_methods.length) payload.payout_methods = f.payout_methods;
      if (f.include_hidden) payload.include_hidden = true;
      const dest = this.splitCsv(this.destinationCountries);
      if (dest) payload.destination_countries = dest;
      const src = this.splitCsv(this.sourceCountries);
      if (src) payload.source_countries = src;
      const sc = this.splitCsv(this.sendingCurrencies);
      if (sc) payload.sending_currencies = sc;
      const rc = this.splitCsv(this.receivingCurrencies);
      if (rc) payload.receiving_currencies = rc;
      return payload;
    },
    async generate() {
      this.message = "";
      const res = await this.$store.dispatch("statement/requestStatement", this.buildPayload());
      // ARCH relays the mobile service status (201 on success). Also accept
      // body.status === true in case an older proxy always returned HTTP 200.
      const ok = res && (res.status === 201 || (res.status === 200 && res.data && res.data.status === true));
      if (ok) {
        const matched = res.data && res.data.data ? res.data.data.matched_transactions : undefined;
        this.messageType = "success";
        this.message =
          "Statement queued successfully" +
          (matched !== undefined ? ` (${matched} transactions matched). It will appear below shortly.` : ".");
        await this.refreshList();
        this.startPolling();
      } else {
        this.messageType = "error";
        this.message = this.extractError(res) || "Failed to generate statement.";
      }
    },
    async refreshList() {
      if (!this.customerId) return;
      const res = await this.$store.dispatch("statement/listStatements", this.customerId);
      if (res && res.status === 200 && res.data) {
        this.statements = res.data.data || [];
        if (this.statements.some((s) => ["pending", "processing"].includes(s.status))) {
          this.startPolling();
        } else {
          this.stopPolling();
        }
      } else if (res && res.status === 401) {
        this.messageType = "error";
        this.message = "You do not have permission to view statements.";
      }
    },
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => this.refreshList(), 5000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    extractError(res) {
      if (!res || !res.data) return "";
      return res.data.detail || res.data.message || "";
    },
    statusBadgeClass(status) {
      switch (status) {
        case "ready":
          return "badge-success";
        case "failed":
          return "badge-danger";
        case "processing":
          return "badge-info";
        default:
          return "badge-secondary";
      }
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
    },
  },
};
</script>
